// 젬 상점 표시 데이터(순수 로직 — DOM·fetch 없음). 문구·가격 표기·오류 안내·보이기 판정.

export interface IapProductView {
  id: string; kind: 'gems' | 'monthly' | 'pass'; name: string; price: number; paidGems: number; bonusGems: number; firstBonusGems: number;
  dailyGems: number; days: number; enabled: boolean; blurb: string;
  /** 계정당 구매 횟수(0/없음 = 제한 없음) · Play Console 인앱 상품 id(없으면 Play에서 안 판다) */
  perAccount?: number; playSku?: string | null;
}
export interface IapConfigView {
  live: boolean; storeId: string; channelKey: string; currency: string; withdrawDays: number; products: IapProductView[];
  /** 웹 포트원 결제를 받는가(옛 서버 응답에는 없다 → 받는 것으로 본다) */
  web?: boolean;
  /** Google Play 결제 단계(없으면 null) */
  play?: { stage: 'test' | 'live' } | null;
}
export interface IapWalletView {
  paid: number; free: number; total: number; debt: number;
  monthly: { endsAt: number; daily: number; claimedToday: boolean } | null;
  refundPending: boolean;
  /** 이번 달(한국 달력) 결제 한도 현황(서버 계산, 없으면 숨김) */
  monthLimit?: { spent: number; limit: number; remaining: number };
}
export interface IapOrderView {
  id: string; productId: string; name: string; amount: number; status: string; mode: string; refundState: string | null;
  createdAt: number; paidAt: number | null; withdrawUntil: number | null; withdrawable: boolean;
}

export const won = (n: number): string => n.toLocaleString('ko-KR') + '원';
export const gems = (n: number): string => n.toLocaleString('ko-KR');

/** 상점 탭을 보일지: 서버 설정이 있고(비밀 4개), 실결제이거나 테스트 결제 시험자(?iap=test)일 때만 — 가안 가격을 일반 이용자에게 공개하지 않는다 */
export function shopVisible(cfg: IapConfigView | null, tester: boolean): boolean {
  return !!cfg && (cfg.live || tester);
}

export type ShopMode = 'web' | 'play' | 'app-notice';
export interface ShopModeInput { app: boolean; tester: boolean; playSupported: boolean }
/**
 * 상점 탭의 동작: 'play' = 앱(Play 설치)에서 Play Billing, 'web' = 웹 포트원, 'app-notice' = 앱인데 Play를 못 쓸 때 안내만, null = 탭 숨김.
 * Play 단계가 test면 테스터(?iap=test 세션)에게만 보인다.
 */
export function shopMode(cfg: IapConfigView | null, i: ShopModeInput): ShopMode | null {
  if (!cfg) return null;
  if (i.app) {
    const playOpen = !!cfg.play && i.playSupported && (cfg.play.stage === 'live' || i.tester);
    if (playOpen) return 'play';
    return shopVisible(cfg, i.tester) ? 'app-notice' : null;
  }
  return cfg.web !== false && shopVisible(cfg, i.tester) ? 'web' : null;
}

/** 구매 시트의 미성년자 고지 문구(서버 minorNotice 확인과 짝) */
export const MINOR_NOTICE_TEXT = '만 19세 미만은 법정대리인 동의가 필요하며, 동의 없는 결제는 취소할 수 있습니다. 계정당 한 달 결제 한도 70만 원.';
export const MINOR_CHECK_LABEL = '위 내용을 확인했어요(만 19세 미만은 법정대리인 동의를 받았어요)';
export const MINOR_UNCHECKED_ERROR = '미성년자 결제 안내를 확인하고 체크해 주세요.';

/** 상품 종류별 청약철회 한 줄(카드·구매 시트·/pricing 공통). 시즌 패스는 판매 전이라 null */
export const WITHDRAW_NOT_AFTER_USE = '사용한 젬·받은 일일 젬은 철회 불가(전자상거래법 17조 2항)';
export function withdrawShort(kind: IapProductView['kind'], days = 7): string | null {
  if (kind === 'gems') return `${days}일 이내·미사용 시 청약철회 가능`;
  if (kind === 'monthly') return `${days}일 이내·일일 젬을 받기 전까지 청약철회 가능`;
  return null;
}

/** 지갑 줄 옆 "이번 달 결제 가능 n원 남음" — 현황이 없으면 null(숨김) */
export function monthLimitText(w: IapWalletView | null): string | null {
  const m = w?.monthLimit;
  if (!m || !Number.isFinite(m.remaining)) return null;
  return `이번 달 결제 가능 ${won(Math.max(0, m.remaining))} 남음`;
}

/** 카드 한 장의 줄들 */
export function productLines(p: IapProductView, firstBought: boolean): { title: string; detail: string; badge: string | null; price: string; cta: string } {
  const badge = !p.enabled ? '준비 중' : p.firstBonusGems > 0 && !firstBought ? '첫 결제 +' + gems(p.firstBonusGems) : null;
  const detail = p.kind === 'monthly' ? `즉시 ${gems(p.paidGems)} · ${p.days}일간 매일 ${gems(p.dailyGems)}`
    : p.kind === 'pass' ? p.blurb
    : `젬 ${gems(p.paidGems)}` + (p.bonusGems ? ` + 덤 ${gems(p.bonusGems)}` : '');
  return { title: p.name, detail, badge, price: won(p.price), cta: p.enabled ? '구매' : '준비 중' };
}

export interface ProductExtras {
  /** 「첫 구매 2배」 같은 배지(아직 그 상품을 산 적 없고 첫 구매 보너스가 있을 때만) */
  firstBadge: string | null;
  /** 덤 비율 표기(덤 젬 ÷ 유료 젬 실제 계산값, 덤 없으면 null) */
  bonusRatio: string | null;
  /** 보조 문구(계정당 1회 · 자동 갱신 없음) */
  note: string | null;
  /** 계정당 한정 상품을 이미 샀음 → 「구매 완료」 비활성 */
  owned: boolean;
}
export function productExtras(p: IapProductView, bought: boolean): ProductExtras {
  const once = (p.perAccount ?? 0) > 0;
  const firstBadge = p.enabled && p.firstBonusGems > 0 && !bought
    ? (p.paidGems > 0 && p.firstBonusGems === p.paidGems ? '첫 구매 2배' : '첫 구매 +' + gems(p.firstBonusGems))
    : null;
  const bonusRatio = p.kind === 'gems' && p.paidGems > 0 && p.bonusGems > 0 ? `덤 ${Math.round((p.bonusGems / p.paidGems) * 100)}%` : null;
  const note = once ? (p.perAccount === 1 ? '계정당 1회' : `계정당 ${p.perAccount}회`) : p.kind === 'monthly' ? '자동 갱신 없음' : null;
  return { firstBadge, bonusRatio, note, owned: once && bought };
}

/** 서버 오류 코드 → 사용자 문구 */
export function errorText(code: string | null | undefined): string {
  if (code === 'app-channel') return '앱에서는 결제를 준비 중입니다.';
  switch (code) {
    case 'payment-pending': return '결제 확인이 아직 끝나지 않았어요. 잠시 뒤 상점을 다시 열면 자동으로 확인해요.';
    case 'payment-failed': return '결제가 실패했어요. 청구되지 않았어요.';
    case 'payment-cancelled': return '결제가 취소됐어요.';
    case 'payment-mismatch': return '결제 정보가 주문과 달라 지급을 멈췄어요. 고객센터가 확인 후 처리해요.';
    case 'portone-unavailable': return '결제사 응답이 늦어요. 잠시 뒤 다시 확인해 주세요(중복 지급·청구 없음).';
    case 'link-required': return '구매 보호를 위해 먼저 설정 → 계정에서 Google로 연결해 주세요.';
    case 'insufficient': return '젬이 모자라요.';
    case 'not-eligible': return '조건에 맞지 않아요.';
    case 'rate': return '잠시 뒤 다시 시도해 주세요.';
    case 'no-session': return '계정 연결을 확인하는 중이에요. 잠시 뒤 다시 시도해 주세요.';
    case 'iap-test-restricted': return '지금은 테스트 결제 시험자만 구매할 수 있어요.';
    case 'iap-notice-required': return '미성년자 결제 안내를 확인해 주세요.';
    case 'iap-monthly-limit': return '이번 달 결제 한도(70만 원)를 넘어 결제할 수 없어요. 다음 달 1일에 다시 열려요.';
    case 'iap-disabled': return '지금은 상점을 열 수 없어요.';
    case 'iap-once': return '계정당 한 번만 살 수 있는 상품이에요.';
    case 'play-unavailable': return 'Google Play 응답이 늦어요. 잠시 뒤 다시 확인해 주세요(중복 지급·청구 없음).';
    case 'token-used': return '이미 처리된 구매예요.';
    case 'user-cancel': return '결제를 취소했어요. 청구되지 않았어요.';
    default: return '결제를 끝내지 못했어요. 잠시 뒤 다시 시도해 주세요.';
  }
}

/** 구매 내역 상태 글자 */
export function orderStatusText(o: IapOrderView): string {
  if (o.refundState === 'requested') return '환불 요청 접수';
  if (o.refundState === 'approved') return '환불 처리 중(결제 취소 대기)';
  if (o.refundState === 'rejected') return '지급 완료 · 환불 요청 반려(고객센터 문의)';
  switch (o.status) {
    case 'paid': return '지급 완료';
    case 'refunded': return '환불됨';
    case 'cancelled': return '취소됨';
    case 'failed': return '실패';
    case 'review': return '확인 중(고객센터)';
    default: return o.status;
  }
}

/** 포트원 결제창 응답의 code → 사용자 취소인지 */
export function isUserCancel(code: string | undefined, message: string | undefined): boolean {
  return code === 'FAILURE_TYPE_PG' && /취소|cancel/i.test(message ?? '') || code === 'PAY_PROCESS_CANCELED';
}

/** 구매자 입력(KG이니시스 필수: 이름·휴대폰·이메일) 형식 검사 — 결제사에만 보내고 게임 서버에 저장하지 않는다 */
export function buyerError(b: { name: string; phone: string; email: string }): string | null {
  if (!b.name.trim() || b.name.trim().length > 30) return '이름을 입력해 주세요.';
  if (!/^01[0-9]-?\d{3,4}-?\d{4}$/.test(b.phone.trim())) return '휴대폰 번호를 확인해 주세요.';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(b.email.trim())) return '이메일을 확인해 주세요.';
  return null;
}
